import { useState } from 'react';
import { motion } from 'framer-motion';
import type { Scenario, ScenarioOutput } from '@zg/shared';
import { formatCurrency } from '../../lib/format';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export interface ScenarioBandProps {
  best: ScenarioOutput;
  average: ScenarioOutput;
  conservative: ScenarioOutput;
}

const ORDER: Scenario[] = ['best', 'average', 'conservative'];
const LABELS: Record<Scenario, string> = {
  best: 'BEST_CASE',
  average: 'AVERAGE',
  conservative: 'CONSERVATIVE',
};

const DOT_COLORS: Record<Scenario, string> = {
  best: '#60A5FA',
  average: '#3B82F6',
  conservative: '#1D4ED8',
};

function positionFor(scenario: Scenario, totals: Record<Scenario, number>): number {
  const lo = totals.best;
  const hi = totals.conservative;
  const v = totals[scenario];
  if (hi <= lo) return scenario === 'best' ? 0.05 : scenario === 'conservative' ? 0.95 : 0.5;
  const raw = (v - lo) / (hi - lo);
  return 0.05 + raw * 0.9;
}

/**
 * Multi-layer gradient area visualization — the NEXA "Compute Allocation"
 * motif applied to scenario totals. Three stacked translucent blue areas
 * with dot markers along the top edge.
 */
export function ScenarioBand({ best, average, conservative }: ScenarioBandProps) {
  const reduced = useReducedMotion() ?? false;
  const [hover, setHover] = useState<Scenario | null>(null);

  const totals: Record<Scenario, number> = {
    best: best.total,
    average: average.total,
    conservative: conservative.total,
  };
  const outputs: Record<Scenario, ScenarioOutput> = { best, average, conservative };

  const positions: Record<Scenario, number> = {
    best: positionFor('best', totals),
    average: positionFor('average', totals),
    conservative: positionFor('conservative', totals),
  };

  // Area chart geometry — 3 layers, each a smooth curve from left to its scenario x.
  // We render as SVG path fills with gradients per layer.
  const VB_W = 1000;
  const VB_H = 180;

  const curvePath = (xPct: number): string => {
    const endX = Math.max(20, Math.min(VB_W - 1, xPct * VB_W));
    const peakY = 40;
    const baseY = VB_H;
    // Smooth bell: quadratic bezier from (0, baseY) up through midpoint and back down at endX, then baseline back.
    const midX = endX / 2;
    return `M 0 ${baseY} Q ${midX * 0.6} ${peakY + 30} ${midX} ${peakY + 10} T ${endX} ${baseY} L 0 ${baseY} Z`;
  };

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-auto"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="zg-layer-best" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#60A5FA" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="zg-layer-avg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="zg-layer-cons" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1D4ED8" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#1D4ED8" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Bottom-most = conservative (widest/deepest) */}
        <motion.path
          d={curvePath(positions.conservative)}
          fill="url(#zg-layer-cons)"
          initial={reduced ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.0 }}
        />
        <motion.path
          d={curvePath(positions.average)}
          fill="url(#zg-layer-avg)"
          initial={reduced ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
        />
        <motion.path
          d={curvePath(positions.best)}
          fill="url(#zg-layer-best)"
          initial={reduced ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        />
        {/* Horizontal baseline */}
        <line
          x1="0"
          y1={VB_H - 1}
          x2={VB_W}
          y2={VB_H - 1}
          stroke="#E5E8EC"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Dots — absolutely positioned over SVG area */}
      <div className="absolute inset-x-0 top-2 h-4 pointer-events-none">
        {ORDER.map((s, i) => {
          const pct = positions[s] * 100;
          const isActive = hover === s;
          return (
            <motion.button
              key={s}
              type="button"
              aria-label={`${LABELS[s]}: ${formatCurrency(totals[s])}`}
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(s)}
              onBlur={() => setHover(null)}
              onClick={() => setHover((prev) => (prev === s ? null : s))}
              initial={{ opacity: reduced ? 1 : 0, scale: reduced ? 1 : 0.5 }}
              animate={{ opacity: 1, scale: isActive ? 1.2 : 1 }}
              transition={{
                duration: reduced ? 0 : 0.3,
                delay: reduced ? 0 : 0.5 + i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="pointer-events-auto absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-3.5 w-3.5 rounded-full border-2 border-panel shadow-[0_1px_3px_rgba(0,0,0,0.15)] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue"
              style={{ left: `${pct}%`, backgroundColor: DOT_COLORS[s] }}
            />
          );
        })}
      </div>

      {/* Labels */}
      <div className="relative mt-3 h-auto">
        {ORDER.map((s) => {
          const pct = positions[s] * 100;
          const translate =
            s === 'best'
              ? 'translate-x-0'
              : s === 'conservative'
                ? '-translate-x-full'
                : '-translate-x-1/2';
          const align =
            s === 'best' ? 'items-start text-left' : s === 'conservative' ? 'items-end text-right' : 'items-center text-center';
          return (
            <div
              key={s}
              className={`absolute top-0 flex flex-col ${align} ${translate}`}
              style={{ left: `${pct}%` }}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
                &gt; {LABELS[s]}
              </span>
              <span className="mt-1 font-display font-light font-tnum text-[1.25rem] text-ink">
                {formatCurrency(totals[s])}
              </span>
            </div>
          );
        })}
      </div>

      {/* Tooltip */}
      {hover && (
        <div
          role="tooltip"
          className="relative mt-28 mx-auto max-w-md bg-panel border border-hairline rounded-md shadow-panel p-5"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
            &gt; {LABELS[hover]} · BREAKDOWN
          </span>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 font-tnum text-[0.875rem]">
            <dt className="text-ink-muted">Builders Risk</dt>
            <dd className="text-right">{formatCurrency(outputs[hover].builderRisk.total)}</dd>
            <dt className="text-ink-muted">OI General Liability</dt>
            <dd className="text-right">{formatCurrency(outputs[hover].oiGl.total)}</dd>
            <dt className="text-ink-muted">OI Excess $5M</dt>
            <dd className="text-right">{formatCurrency(outputs[hover].oiExcess.total)}</dd>
            <dt className="text-ink-muted">Pollution</dt>
            <dd className="text-right">{formatCurrency(outputs[hover].pollution.total)}</dd>
            <dt className="pt-2 font-medium text-ink">Total</dt>
            <dd className="pt-2 text-right font-medium text-ink">
              {formatCurrency(outputs[hover].total)}
            </dd>
          </dl>
        </div>
      )}
      {!hover && <div className="mt-28" aria-hidden="true" />}
    </div>
  );
}
